import type { TranslationKey } from '@change-corgi/core/i18n';

import type { UploaderControls } from '#src/app/shared/hooks/uppyUploader';
import { getMaxImageFileSize, getUploadErrorMessages } from '#src/app/shared/utils/uppyUploader';

import { MceImageError } from './mceImageError';

type BlobInfo = {
	id: () => string;
	name: () => string;
	filename: () => string;
	blob: () => Blob;
	base64: () => string;
	blobUri: () => string;
	uri: () => string | undefined;
};

export const acceptedRichMediaImageTypes = ['image/gif', 'image/jpeg', 'image/png', 'image/svg+xml', 'image/webp'];

export const imagesUploadHandler =
	({ uploader, setErrorKey }: { uploader: UploaderControls; setErrorKey: (errorKey: TranslationKey | null) => void }) =>
	async (blobInfo: BlobInfo): Promise<string> => {
		if (blobInfo.blob().size > getMaxImageFileSize()) {
			setErrorKey(getUploadErrorMessages().IMAGE_TOO_LARGE);
			throw new MceImageError({ remove: true });
		}
		if (!acceptedRichMediaImageTypes.includes(blobInfo.blob().type)) {
			setErrorKey(getUploadErrorMessages().UNSUPPORTED_FILE_TYPE);
			throw new MceImageError({ remove: true });
		}
		uploader.addFile(blobInfo.blob().type, blobInfo.blob());
		const result = await uploader.upload();
		// `result` itself is genuinely `UploadResultType | undefined` (see uppyUploader/types.ts).
		// `.at(0)` (unlike the `[0]` index signature) is correctly typed `UppyFile | undefined`,
		// matching the real runtime possibility of an empty `successful` array.
		const filePublicUrl = result?.successful?.at(0)?.meta.filePublicUrl;
		if (typeof filePublicUrl !== 'string' || !filePublicUrl) {
			setErrorKey(getUploadErrorMessages().CATCH_ALL);
			throw new MceImageError({ remove: true });
		}
		return decodeURIComponent(filePublicUrl);
	};
